/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Inventor Automation team
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that the above copyright notice appears in all copies and
// that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////

import * as signalR from '@microsoft/signalr';
import repo from './Repository';

class JobManager {

    async startConnection() {
        const connection = new signalR.HubConnectionBuilder()
            .withUrl('/signalr/connection', { accessTokenFactory: () => repo.getAccessCode() ?? "" }
        )
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        await connection.start().catch(err => {

            if (err.statusCode === 401 || err?.message?.includes("401")) {
                console.warn("Unauthorized to connect to SignalR hub. Access code might be expired.");
            }
        });

        return connection;
    }

    async doUpdateJob(projectId, parameters, onStart, onComplete, onError) {
        const connection = await this.startConnection();

        if (onStart)
            onStart();

        connection.on("onComplete", (updatedState, stats, reportUrl) => {
            // stop connection
            //connection.stop();

            if (onComplete)
                onComplete(updatedState, stats, reportUrl);
        });

        connection.on("onError", (errorData) => {
            connection.stop();

            if (onError)
                onError(errorData);
        });

        await connection.invoke('CreateUpdateJob', projectId, parameters, repo.getAccessCode());
        //await connection.stop();
    }


    async doAdoptJob(packageId, onStart, onComplete, onError) {
        const connection = await this.startConnection();
        

        if (onStart)
            onStart();

        connection.on("onComplete", (newProject, stats, reportUrl) => {
            // stop connection
            //connection.stop();

            if (onComplete)
                onComplete(newProject,stats,reportUrl);
        });

        connection.on("onError", (errorData) => {
            connection.stop();

            if (onError)
                onError(errorData);
        });

        await connection.invoke('CreateAdoptJob', packageId, repo.getAccessCode());
    }

    async doAdoptWithParameters(parameters, onStart, onComplete, onError) {
        const connection = await this.startConnection();

        if (onStart)
            onStart();

        connection.on("onComplete", (newProject) => {
            // stop connection
            //connection.stop();

            if (onComplete)
                onComplete(newProject);
        });

        connection.on("onError", (jobId, reportUrl) => {
            connection.stop();

            if (onError)
                onError(jobId, reportUrl);
        });

        await connection.invoke("CreateAdoptProjectWithParametersJob", parameters, repo.getAccessCode());
    }

    /**
     * Generic way to generate a download and get URL to it.
     *
     * @param methodName SignalR method to call.
     * @param projectId  Project ID.
     * @param hash       Parameters hash.
     * @param onStart    Callback to be called on start. No arguments.
     * @param onSuccess  Callback to be called on success. Argument: url to the generated download.
     * @param onError    Callback to be called on error. Arguments: job ID, report url.
     * */
    async doDownloadJob(methodName, projectId, hash, key, onStart, onSuccess, onError) {

        const connection = await this.startConnection();

        if (onStart) onStart();

        connection.on("onComplete", (downloadUrl, stats, reportUrl) => {

            //connection.stop();

            if (! downloadUrl && ! stats) {
                if (onError) onError("no outputs", "Downloads are not found");
                return;
            }

            if (onSuccess) {
                const code = repo.getAccessCode();
                if (code) {
                    downloadUrl += "/" + code;
                }
                onSuccess(downloadUrl, stats, reportUrl);
            }
        });

        connection.on("onError", (errorData) => {

            connection.stop();
            if (onError) onError(errorData);
        });

        if (key != null)
            await connection.invoke(methodName, projectId, hash, key, repo.getAccessCode());
        else
            await connection.invoke(methodName, projectId, hash, repo.getAccessCode());
    }

    async doDrawingExportJob(projectId, hash, drawingKey, onStart, onComplete, onError) {
        const connection = await this.startConnection();

        if (onStart)
            onStart();

        connection.on("onComplete", (drawingUrl, stats, reportUrl) => {
            // stop connection
            //connection.stop();

            if (onComplete) {
                onComplete(drawingUrl, stats, reportUrl);
            }
        });

        connection.on("onError", (errorData) => {
            connection.stop();

            if (onError)
                onError(errorData);
        });

        await connection.invoke('CreateDrawingPdfJob', projectId, hash, drawingKey, repo.getAccessCode());
    }
}

const jobManager = new JobManager();

export function Jobs() {
  return jobManager;
}


